/**
 * SubmissionFilesDrawer Component
 * =================================
 * Admin drawer/modal for reviewing submission eBook files.
 * Displays file list with integrity badges, preservation status, and reader.
 */

import { useState, useEffect } from 'react';
import { 
  X, 
  FileText, 
  BookOpen, 
  Book, 
  Download, 
  Eye,
  ShieldCheck,
  Shield,
  Lock,
  AlertCircle,
  Loader2,
  Check,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import ebookService from '@/services/ebookService';
import authService from '@/services/authService';

const FILE_ICONS = {
  '.pdf': FileText,
  '.epub': BookOpen,
  '.mobi': Book
};

const PRESERVATION_COLORS = {
  'UPLOADED': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'VERIFIED': 'bg-green-500/20 text-green-300 border-green-500/30',
  'ENCRYPTED': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'FAILED': 'bg-red-500/20 text-red-300 border-red-500/30'
};

const PRESERVATION_ICONS = {
  'UPLOADED': Shield,
  'VERIFIED': ShieldCheck,
  'ENCRYPTED': Lock,
  'FAILED': AlertCircle
};

export default function SubmissionFilesDrawer({ 
  isOpen, 
  onClose, 
  submission,
  onVerifySuccess 
}) {
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [verifying, setVerifying] = useState(null);
  const [readerAsset, setReaderAsset] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  useEffect(() => {
    if (isOpen && submission?.id) {
      loadAssets();
    }
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
    };
  }, [isOpen, submission?.id]);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const data = await ebookService.adminListEbooks(submission.id);
      setAssets(data || []);
    } catch (error) {
      console.error('Error loading ebooks:', error);
      toast.error('Failed to load files');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (asset) => {
    setVerifying(asset.id);
    try {
      await ebookService.adminVerifyEbook(submission.id, asset.id);
      toast.success('eBook verified successfully');
      
      // Update local state
      setAssets(prev => prev.map(a => 
        a.id === asset.id 
          ? { ...a, preservation_status: 'VERIFIED', verified_at: new Date().toISOString() }
          : a
      ));
      
      if (onVerifySuccess) onVerifySuccess();
    } catch (error) {
      toast.error(error.message || 'Failed to verify');
    } finally {
      setVerifying(null);
    }
  };

  const handleOpenReader = async (asset) => {
    if (asset.ext === '.mobi') {
      handleDownload(asset);
      return;
    }

    setReaderAsset(asset);
    
    // Load PDF/EPUB
    if (asset.ext === '.pdf') {
      try {
        const token = authService.getAccessToken();
        const url = ebookService.getStreamUrl(asset.id);
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(blobUrl);
        }
      } catch (error) {
        toast.error('Failed to load PDF');
      }
    }
  };

  const handleDownload = async (asset) => {
    const token = authService.getAccessToken();
    const url = ebookService.getDownloadUrl(asset.id);
    
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = asset.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const closeReader = () => {
    setReaderAsset(null);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  if (!submission) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-2xl bg-slate-900 border-slate-700 text-white overflow-y-auto"
      >
        <SheetHeader className="border-b border-slate-700 pb-4">
          <SheetTitle className="text-white flex items-center gap-3">
            <FileText className="w-5 h-5 text-purple-400" />
            <span>Files for: {submission.title}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No eBook files uploaded yet</p>
            </div>
          ) : readerAsset ? (
            // Reader View
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">{readerAsset.filename}</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={closeReader}
                  className="border-slate-600"
                >
                  <X className="w-4 h-4 mr-1" />
                  Close Reader
                </Button>
              </div>
              
              {readerAsset.ext === '.pdf' && pdfBlobUrl && (
                <iframe
                  src={`${pdfBlobUrl}#toolbar=1`}
                  className="w-full h-[500px] rounded-lg border border-slate-700"
                  title="PDF Viewer"
                />
              )}
              
              {readerAsset.ext === '.epub' && (
                <div className="p-8 text-center text-slate-400 border border-slate-700 rounded-lg">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>EPUB viewer requires full-page modal</p>
                  <Button
                    onClick={() => handleDownload(readerAsset)}
                    className="mt-4"
                    variant="outline"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download EPUB
                  </Button>
                </div>
              )}
            </div>
          ) : (
            // File List
            <div className="space-y-3">
              {assets.map((asset) => {
                const FileIcon = FILE_ICONS[asset.ext] || FileText;
                const PreservationIcon = PRESERVATION_ICONS[asset.preservation_status] || Shield;
                
                return (
                  <div
                    key={asset.id}
                    className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className="w-12 h-14 rounded-lg bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-blue-500/20 flex items-center justify-center">
                        <FileIcon className="w-6 h-6 text-purple-400" />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white truncate">
                          {asset.filename}
                        </h4>
                        
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                          <span>{ebookService.formatFileSize(asset.size_bytes)}</span>
                          <span>•</span>
                          <span className="uppercase">{asset.ext?.replace('.', '')}</span>
                          <span>•</span>
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(asset.created_at)}</span>
                        </div>
                        
                        {/* Badges */}
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          {/* Checksum */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                  <Check className="w-3 h-3" />
                                  SHA-256 ✓
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="font-mono text-xs break-all max-w-xs">
                                {asset.checksum_sha256}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          {/* Preservation Status */}
                          <Badge className={`${PRESERVATION_COLORS[asset.preservation_status]} border`}>
                            <PreservationIcon className="w-3 h-3 mr-1" />
                            {asset.preservation_status}
                          </Badge>
                          
                          {asset.verified_at && (
                            <span className="text-xs text-green-400">
                              Verified {formatDate(asset.verified_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {asset.ext !== '.mobi' && (
                          <Button
                            size="sm"
                            onClick={() => handleOpenReader(asset)}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(asset)}
                          className="border-slate-600"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        
                        {asset.preservation_status === 'UPLOADED' && (
                          <Button
                            size="sm"
                            onClick={() => handleVerify(asset)}
                            disabled={verifying === asset.id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {verifying === asset.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <ShieldCheck className="w-4 h-4 mr-1" />
                            )}
                            Verify
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
